// Estimate peek — right slide-over preview used by the opportunity room.
// Keeps the user inside the data room's filter context instead of
// navigating away; "Open full estimate" is the explicit exit.

import { useEffect } from "react"
import { Link } from "react-router"
import { AnimatePresence, motion } from "motion/react"
import { XClose, ArrowUpRight, AlertTriangle } from "@untitledui/icons"
import { StatusBadge } from "@/components/estimates/status-badge"
import type { ApiEstimate, VehicleSection } from "@/lib/api/types"
import { vehicleDisplayName, estimateCustomerName } from "@/lib/api/types"
import { isDeferred } from "@/lib/api/estimates"

const SECTION_LABELS: Partial<Record<VehicleSection, string>> = {
  FRONT_BUMPER_GRILLE: "Front bumper/grille", REAR_BUMPER: "Rear bumper", FENDER: "Fender",
  FRONT_DOOR: "Front door", REAR_DOOR: "Rear door", HOOD: "Hood", TRUNK: "Trunk", ROOF: "Roof",
  FRONT_LAMPS: "Front lamps", REAR_LAMPS: "Rear lamps", QUARTER_PANEL: "Quarter panel",
  ROCKER_PANEL: "Rocker panel", LIFT_GATE: "Lift gate", TAIL_GATE: "Tail gate", WHEELS: "Wheels",
  FRAME: "Frame", TRUNK_LID: "Trunk lid", PICKUP_BOX: "Pickup box", WINDSHIELD: "Windshield",
}

const fmt$full = (n: number) => `$${Math.round(n).toLocaleString()}`
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-zinc-100 last:border-b-0">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 shrink-0">{label}</p>
      <p className="text-sm text-zinc-800 text-right">{children}</p>
    </div>
  )
}

export function EstimatePeek({ estimate, sourceLabel, onClose }: {
  estimate: ApiEstimate | null
  sourceLabel: string | null
  onClose: () => void
}) {
  useEffect(() => {
    if (!estimate) return
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [estimate, onClose])

  const structural = estimate?.lineItems?.some(li => li.structuralInvolvement) ?? false

  // Sections rollup: count + worst severity per section
  const sections = estimate
    ? Array.from(
        estimate.lineItems.reduce((m, li) => {
          const cur = m.get(li.section) ?? { count: 0, severity: 0, structural: false }
          m.set(li.section, {
            count: cur.count + 1,
            severity: Math.max(cur.severity, li.severity ?? 0),
            structural: cur.structural || !!li.structuralInvolvement,
          })
          return m
        }, new Map<VehicleSection, { count: number; severity: number; structural: boolean }>()),
      )
    : []

  const ageH = estimate ? (Date.now() - new Date(estimate.createdAt).getTime()) / 3600000 : 0

  return (
    <AnimatePresence>
      {estimate && (
        <>
          <motion.button
            aria-label="Close preview"
            className="fixed inset-0 z-40 bg-zinc-900/20"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            role="dialog" aria-label="Estimate preview"
            className="fixed right-0 inset-y-0 z-50 w-full max-w-md bg-white shadow-2xl border-l border-zinc-200 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.22, ease: "easeOut" }}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-5 border-b border-zinc-100">
              <div>
                <p className="text-lg font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "var(--font-headline)" }}>
                  {vehicleDisplayName(estimate.vehicle)}
                </p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <StatusBadge status={estimate.status} />
                  {isDeferred(estimate) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider">
                      Deferred
                    </span>
                  )}
                  {structural && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-wider">
                      <AlertTriangle className="size-3" aria-hidden /> Structural
                    </span>
                  )}
                </div>
              </div>
              <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors">
                <XClose className="size-5" aria-hidden />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="rounded-xl bg-zinc-50 border border-zinc-200/50 p-4 mb-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Estimated value</p>
                <p className="text-3xl font-bold tabular-nums text-zinc-900 tracking-tight mt-1">{fmt$full(estimate.avg ?? 0)}</p>
                {estimate.min > 0 && estimate.max > 0 && (
                  <p className="text-xs text-zinc-400 tabular-nums mt-1">range {fmt$full(estimate.min)} – {fmt$full(estimate.max)}</p>
                )}
              </div>

              <Row label="Customer">{estimateCustomerName(estimate) || "—"}</Row>
              <Row label="Advisor">{estimate.createdBy ? `${estimate.createdBy.firstName} ${estimate.createdBy.lastName}` : "Unassigned"}</Row>
              {sourceLabel && <Row label="Channel">{sourceLabel}</Row>}
              {estimate.dealership?.name && <Row label="Store">{estimate.dealership.name}</Row>}
              <Row label="Created">{fmtDate(estimate.createdAt)}{ageH > 24 && ["Pending", "Confirmed", "InProgress", "Scheduled"].includes(estimate.status) && (
                <span className="text-amber-600 font-medium"> · waiting {ageH < 48 ? `${Math.round(ageH)}h` : `${Math.round(ageH / 24)}d`}</span>
              )}</Row>
              <Row label="Scheduled">{fmtDate(estimate.scheduledAt)}</Row>

              {sections.length > 0 && (
                <div className="mt-5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Damage sections</p>
                  <div className="space-y-1.5">
                    {sections.map(([section, s]) => (
                      <div key={section} className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
                        <p className="text-sm text-zinc-800">
                          {SECTION_LABELS[section] ?? section}
                          {s.structural && <span className="ml-1.5 text-[10px] font-bold text-red-600 uppercase">structural</span>}
                        </p>
                        <p className="text-xs text-zinc-400 tabular-nums shrink-0">
                          {s.count} item{s.count !== 1 ? "s" : ""}{s.severity > 0 && <> · sev {s.severity}</>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-zinc-100">
              <Link to={`/estimates/${estimate.id}`}
                className="flex items-center justify-center gap-1.5 h-10 w-full rounded-xl bg-zinc-900 text-sm font-medium text-white hover:bg-black transition-colors">
                Open full estimate <ArrowUpRight className="size-4" aria-hidden />
              </Link>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
